<style lang="scss" src="./VProgressBar.scss"/>

<script lang="ts">

import Decimal from 'decimal.js';
import Core from '@/core/Core';
import type Numeric from '@/core/types/Numeric';
import ProgressBarModel from './ProgressBarModel';
import type { NumericConstructor } from './ProgressBarModel';
import type ProgressBarSegment from './ProgressBarSegment';
import { PROGRESS_COLORS } from './ProgressBarSegment';
import type { ProgressColor } from './ProgressBarSegment';

/**
 * Минимальная ширина в пикселях ненулевого сегмента, current и остатка.
 *
 * Гарантирует что:
 * - любое ненулевое значение видно (1% на узком баре не виден);
 * - неполный бар выглядит неполным: у ненулевого остатка тоже есть минимум,
 *   поэтому 99.9% не рисуется как 100%.
 *
 * Ширины задаются через flex-grow с нулевым basis. Если минимумы не влезают,
 * браузер сам забирает недостающее у остальных элементов пропорционально,
 * сумма никогда не вылезает за ширину бара.
 */
const MIN_WIDTH_PX = 3;

/** Цвет подсвеченной части сегмента (current) */
const CURRENT_COLOR: ProgressColor = 'yellow';

/**
 * VProgressBar — прогресс-бар для финансовых и количественных данных.
 *
 * Состав задаётся вложенными тегами, как колонки в VDataGridV2:
 * компонент читает vnode слота и сам считает сумму, остаток и ширины.
 *
 * Правила:
 * - total не задан — 100% это сумма сегментов;
 * - сумма сегментов больше total — она становится новым total;
 * - overflow задан — превышение над total считается перегрузом: хвост бара
 *   сверх лимита заливается цветом overflow, справа подпись «+превышение»;
 *   не задан — превышение нормально (заказы с пересортом), подсветки нет;
 * - отрицательные значения берутся по модулю;
 * - 0 рисуется нулём, total — полным баром; всё ненулевое и всё неполное
 *   имеет минимальную видимую ширину (см. MIN_WIDTH_PX).
 *
 * Подписи:
 * - слева сумма сегментов, справа остаток до total, а без total — прочерк; считаются всегда сами,
 *   слотов на замену нет — подпись-заголовок выносится за пределы бара;
 * - форматируются через format() семантического типа, если он есть в баре,
 *   иначе как число с 3 знаками;
 * - shorten (по умолчанию) — сокращённо: 12,5 тыс, 3,45 млн, 1,2 млрд (formatShorten);
 *   полные значения тогда уходят в title бара;
 * - unit (строка, единица измерения или валюта) выводится один раз справа от бара,
 *   а не в каждой подписи: в баре и так тесно;
 * - слот #description — подпись под баром.
 *
 * Событие open — клик по бару, аргумент — DOM-элемент бара (якорь для попапа).
 *
 * @example
 * <VProgressBar
 *   :total="row.paymentStatus.total"
 *   :unit="row.currency"
 *   @open="(anchor) => OrdersService.showPositionPaymentsPopup(row, anchor)"
 * >
 *   <VProgressBarSegment :value="row.paymentStatus.payed" color="green"/>
 *   <VProgressBarSegment :value="row.paymentStatus.inQueue" color="blue"/>
 *
 *   <template #description>
 *     Всего: {{ row.paymentStatus.total.format(row.currency) }}
 *   </template>
 * </VProgressBar>
 *
 * Загрузка ТС — превышение лимита недопустимо:
 * <VProgressBar :total="vehicle.maxWeight" :unit="vehicle.unit" overflow="critical">
 *   <VProgressBarSegment :value="cargoWeightGross" color="green"/>
 * </VProgressBar>
 */
export default defineComponent({
  name: 'VProgressBar',

  props: {
    /**
     * Общий объём (100%). Не задан — берётся сумма сегментов.
     * Число или семантический тип (наследник Numeric).
     */
    total: {
      type:    [Object, Number] as PropType<Numeric | number | null>,
      default: null,
    },

    /**
     * Единица справа от бара: строка, единица измерения или валюта.
     * Объект приводится через toString(). null — единицы нет.
     */
    unit: {
      type:    [String, Object] as PropType<string | { toString(): string } | null>,
      default: null,
    },

    /**
     * Цвет перегруза. Задан — сумма больше total считается превышением
     * лимита и подсвечивается этим цветом. null — превышение нормально,
     * total просто расширяется до суммы.
     */
    overflow: {
      type:      String as PropType<ProgressColor | null>,
      default:   null,
      validator: (v: string) => (PROGRESS_COLORS as readonly string[]).includes(v),
    },

    /**
     * Сокращать подписи до тыс / млн / млрд. Включено по умолчанию: в баре мало места,
     * а суммы в сумах идут на миллиарды. Полные значения — в title бара.
     */
    shorten: {
      type:    Boolean,
      default: true,
    },

    /**
     * Раскладка, как у VDataGridV2:
     * - 'auto' — ширина по умолчанию (175–220px), для колонок реестров;
     * - 'fill' — на всю ширину контейнера, для карточек и панелей.
     */
    layout: {
      type:      String as PropType<'auto' | 'fill'>,
      default:   'auto',
      validator: (v: string) => ['auto', 'fill'].includes(v),
    },
  },

  emits: ['open'],

  computed: {
    /** Единица справа от бара; пустая строка — не выводим */
    unitText(): string {
      if (this.unit === null || this.unit === undefined) {
        return '';
      } else {
        return this.unit.toString();
      }
    },

    /** Слушает ли кто-нибудь open — от этого зависят курсор и hover бара */
    isClickable(): boolean {
      return Boolean(this.$.vnode.props?.onOpen);
    },
  },

  methods: {
    // ==================== Отрисовка ====================

    /**
     * Подпись значения: format() семантического типа или число с 3 знаками.
     * Без unit: единица выводится один раз справа от бара (см. unitText).
     *
     * isShorten — сокращённый вид (formatShorten / formatDecimalShorten).
     * Параметром, а не this.shorten: для title нужны полные значения
     * при включённом сокращении.
     */
    formatValue(value: Decimal, numericConstructor: NumericConstructor | null, isShorten: boolean): string {
      let result: string;

      if (numericConstructor === null) {
        if (isShorten === true) {
          result = Core.utils.number.formatDecimalShorten(value, 3);
        } else {
          result = Core.utils.number.numberFormat(value.toNumber(), 3, true);
        }
      } else {
        if (isShorten === true) {
          result = new numericConstructor(value).formatShorten();
        } else {
          result = new numericConstructor(value).format();
        }
      }

      return result;
    },

    /**
     * Доля part от whole как flex-grow с минимальной шириной.
     * Доли внутри одного контейнера в сумме дают 1, поэтому распределяется
     * вся ширина. Потеря точности toNumber здесь допустима — это пиксели.
     */
    growStyle(part: Decimal, whole: Decimal, minWidthPx: number = MIN_WIDTH_PX): Record<string, string> {
      return {
        flexGrow: String(part.div(whole).toNumber()),
        minWidth: `${minWidthPx}px`,
      };
    },

    renderSegment(segment: ProgressBarSegment, total: Decimal): VNode {
      if (segment.hasCurrent === false) {
        return h('span', {
          class: ['segment', segment.color],
          style: this.growStyle(segment.value, total),
        });
      } else {
        const children: VNode[] = [];

        if (segment.rest.isZero() === false) {
          children.push(h('span', {
            class: 'rest',
            style: this.growStyle(segment.rest, segment.value),
          }));
        }

        children.push(h('span', {
          class: ['current', CURRENT_COLOR],
          style: this.growStyle(segment.current, segment.value),
        }));

        // Минимум сегмента должен вместить минимумы обеих частей
        return h('span', {
          class: ['segment', segment.color],
          style: this.growStyle(segment.value, total, MIN_WIDTH_PX * children.length),
        }, children);
      }
    },

    /**
     * Хвост бара сверх лимита. Абсолютным слоем справа поверх сегментов:
     * превышение — это последние excess от суммы, какому сегменту они
     * принадлежат, неважно. Левая граница слоя — метка лимита.
     */
    renderExcess(model: ProgressBarModel): VNode {
      return h('span', {
        class: ['excess', this.overflow],
        style: {
          width:    `${model.excess.div(model.total).times(100).toNumber()}%`,
          minWidth: `${MIN_WIDTH_PX}px`,
        },
      });
    },

    onClick(event: MouseEvent): void {
      this.$emit('open', event.currentTarget);
    },
  },

  render() {
    const model = ProgressBarModel.fromSlot(this.$slots.default?.() || [], this.total);
    const barChildren: VNode[] = [];

    if (model.total.isZero() === false) {
      for (const segment of model.segments) {
        if (segment.value.isZero() === false) {
          barChildren.push(this.renderSegment(segment, model.total));
        }
      }

      if (model.remainder.isZero() === false) {
        barChildren.push(h('span', {
          class: 'remainder',
          style: this.growStyle(model.remainder, model.total),
        }));
      }

      if (this.overflow !== null && model.hasExcess === true) {
        barChildren.push(this.renderExcess(model));
      }
    }

    const leftContent = this.formatValue(model.sum, model.numericConstructor, this.shorten);
    let rightContent: string;
    let rightFull: string;
    let isRightExcess: boolean;

    if (this.overflow !== null && model.hasExcess === true) {
      rightContent = `+${this.formatValue(model.excess, model.numericConstructor, this.shorten)}`;
      rightFull = `+${this.formatValue(model.excess, model.numericConstructor, false)}`;
      isRightExcess = true;
    } else if (model.hasTotal === true) {
      rightContent = this.formatValue(model.remainder, model.numericConstructor, this.shorten);
      rightFull = this.formatValue(model.remainder, model.numericConstructor, false);
      isRightExcess = false;
    } else {
      rightContent = '--';
      rightFull = '--';
      isRightExcess = false;
    }

    // Сокращённые подписи теряют точность — полные значения при наведении
    let barTitle: string | null;

    if (this.shorten === true) {
      barTitle = `${this.formatValue(model.sum, model.numericConstructor, false)} / ${rightFull}`;

      if (this.unitText !== '') {
        barTitle += `\u00A0${this.unitText}`;
      }
    } else {
      barTitle = null;
    }

    barChildren.push(h('span', { class: 'label left' }, leftContent));
    barChildren.push(h('span', { class: { label: true, right: true, exceeded: isRightExcess } }, rightContent));

    const figureChildren: VNode[] = [
      h('div', {
        class:   { bar: true, clickable: this.isClickable },
        title:   barTitle,
        onClick: this.onClick,
      }, barChildren),
    ];

    if (this.unitText !== '') {
      figureChildren.push(h('span', { class: 'unit' }, this.unitText));
    }

    if (this.$slots.description) {
      // Класс description — общий стиль подписи, как у контейнера подписи в старом VLinearDiff
      figureChildren.push(h('figcaption', { class: 'description' }, this.$slots.description()));
    }

    return h('figure', {
      class: { 'v-progress-bar': true, fill: this.layout === 'fill' },
    }, figureChildren);
  },
});
</script>
